import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { RetryMode } from '@src/api/document-api'
import { EProcessingStatus } from '@src/types/automationDocument'
import { DocumentPage } from '@src/types/documentPage'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface RetryDialogProps {
  open: boolean
  allPages: DocumentPage[]
  onClose: () => void
  onConfirm: (mode: RetryMode) => void
  isLoading: boolean
  isCurrentlyProcessing?: boolean
  isFinalizing?: boolean
}

export const RetryDialog = ({ open, allPages, onClose, onConfirm, isLoading, isCurrentlyProcessing, isFinalizing }: RetryDialogProps) => {
  const { t } = useTranslation()

  const allManuallyClassified = useMemo(() => {
    const nonDiscarded = allPages.filter((p) => p.processingStatus !== EProcessingStatus.Discarded)
    return nonDiscarded.length > 0 && nonDiscarded.every((p) => p.documentTypeId != null)
  }, [allPages])

  const [mode, setMode] = useState<RetryMode>(allManuallyClassified ? 'ExtractionOnly' : 'Full')

  const handleConfirm = () => {
    onConfirm(mode)
  }

  const disabled = isLoading || !!isFinalizing

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('docsAutomation.retryTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('docsAutomation.retryDescription')}</DialogContentText>

        {isCurrentlyProcessing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('docsAutomation.documentCurrentlyProcessing' as 'common.close')}
          </Alert>
        )}

        {allManuallyClassified && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('docsAutomation.allPagesManuallyClassified')}
          </Alert>
        )}

        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as RetryMode)}>
          <FormControlLabel
            value="Full"
            control={<Radio />}
            label={t('docsAutomation.retryModeFull')}
            disabled={allManuallyClassified || disabled}
          />
          <FormControlLabel
            value="ClassificationOnly"
            control={<Radio />}
            label={t('docsAutomation.retryModeClassificationOnly')}
            disabled={allManuallyClassified || disabled}
          />
          <FormControlLabel
            value="ExtractionOnly"
            control={<Radio />}
            label={t('docsAutomation.retryModeExtractionOnly')}
            disabled={disabled}
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleConfirm} loading={isLoading} disabled={disabled}>
          {t('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
